/* EMPIRIC single-row color selector (2026-10-17).
   Markup: snippets/product-variant-picker.liquid ([data-empiric-color-row])
   and snippets/product-variant-options.liquid. Styles:
   assets/empiric-color-row.css.

   - Marks each row is-scrollable / at-start / at-end, which drives the edge
     fades and the desktop arrows.
   - Keeps the selected color in view: on load, after every color change, and
     after Dawn swaps the picker (assets/product-info.js replaces the whole
     variant-selects element on each option change, which resets the row's
     scroll position). The last scroll position per picker is restored first,
     then the selected pill is brought into view if it is not already.
   - Mouse drag to scroll on desktop. Touch and trackpads scroll natively.
   - Page load: assets/cro-v1.js preselects Black when the first variant is
     another color, and Dawn then moves focus to that pill after redrawing the
     picker. With no visitor input yet, that focus only scrolls the page down
     to the picker (the media gallery then scrolls back up) and leaves a focus
     ring on the pill, so it is skipped until the visitor first taps, clicks
     or presses a key.
   Loaded once per product form, outside variant-selects; the guard below
   makes repeat loads harmless. */
(function () {
  'use strict';

  if (window.EmpiricColorRow) return;

  var savedScroll = {};
  var reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var visitorActed = false;

  ['pointerdown', 'touchstart', 'keydown'].forEach(function (type) {
    window.addEventListener(
      type,
      function (event) {
        if (event.isTrusted) visitorActed = true;
      },
      { capture: true, passive: true }
    );
  });

  // Skip Dawn's focus() on a pill the visitor did not pick (see header). Only
  // used for pickers Dawn has just redrawn. Once the visitor has acted, focus
  // works as usual.
  function skipScriptFocus(track) {
    if (visitorActed) return;
    var checked = track.querySelector('input[type="radio"]:checked');
    if (!checked) return;
    checked.focus = function () {
      delete checked.focus;
      if (visitorActed) checked.focus.apply(checked, arguments);
    };
  }

  function scrollBehavior(smooth) {
    return smooth && !(reduceMotion && reduceMotion.matches) ? 'smooth' : 'auto';
  }

  function trackOf(row) {
    return row.querySelector('[data-empiric-color-track]');
  }

  function keyOf(row) {
    var picker = row.closest('variant-selects');
    var input = row.querySelector('input[type="radio"]');
    return (picker ? picker.getAttribute('data-section') || '' : '') + '|' + (input ? input.name : '');
  }

  function isShown(el) {
    return !!(el && el.offsetParent !== null && el.clientWidth > 0);
  }

  function update(row) {
    var track = trackOf(row);
    if (!isShown(track)) return;
    var max = track.scrollWidth - track.clientWidth;
    var x = track.scrollLeft;
    var scrollable = max > 1;
    row.classList.toggle('is-scrollable', scrollable);
    row.classList.toggle('at-start', !scrollable || x <= 1);
    row.classList.toggle('at-end', !scrollable || x >= max - 1);
  }

  function scrollTrack(track, left, smooth) {
    if (typeof track.scrollTo === 'function') {
      track.scrollTo({ left: left, behavior: scrollBehavior(smooth) });
    } else {
      track.scrollLeft = left;
    }
  }

  // Bring the checked pill fully into view, clear of the edge fade.
  function reveal(row, smooth) {
    var track = trackOf(row);
    if (!isShown(track)) return;
    var input = track.querySelector('input[type="radio"]:checked');
    var item = input && input.closest('.empiric-color-row__item');
    if (!item) return;

    var view = track.clientWidth;
    var left = track.scrollLeft;
    var start = item.offsetLeft;
    var end = start + item.offsetWidth;
    var room = Math.min(56, view / 6);
    var target = null;

    if (start - room < left) {
      target = start - room;
    } else if (end + room > left + view) {
      target = end + room - view;
    }
    if (target === null) return;

    target = Math.max(0, Math.min(target, track.scrollWidth - view));
    if (Math.abs(target - left) < 1) return;
    scrollTrack(track, target, smooth);
  }

  function bindDrag(row, track) {
    var drag = null;
    var swallowClick = false;

    track.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (track.scrollWidth - track.clientWidth <= 1) return;
      drag = { id: event.pointerId, x: event.clientX, left: track.scrollLeft, moved: false };
    });

    track.addEventListener('pointermove', function (event) {
      if (!drag || event.pointerId !== drag.id) return;
      var dx = event.clientX - drag.x;
      if (!drag.moved) {
        if (Math.abs(dx) < 6) return;
        drag.moved = true;
        row.classList.add('is-dragging');
        try {
          track.setPointerCapture(event.pointerId);
        } catch (error) {
          /* capture is optional */
        }
      }
      track.scrollLeft = drag.left - dx;
    });

    function endDrag() {
      if (!drag) return;
      if (drag.moved) {
        swallowClick = true;
        setTimeout(function () {
          swallowClick = false;
        }, 0);
      }
      drag = null;
      row.classList.remove('is-dragging');
    }

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('lostpointercapture', endDrag);

    // A drag must not also pick the color under the pointer.
    track.addEventListener(
      'click',
      function (event) {
        if (!swallowClick) return;
        swallowClick = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }

  function bind(row, redrawn) {
    if (row.__empiricColorRow) return;
    var track = trackOf(row);
    if (!track) return;
    row.__empiricColorRow = true;

    var key = keyOf(row);
    if (savedScroll[key] != null && isShown(track)) track.scrollLeft = savedScroll[key];
    reveal(row, false);
    update(row);
    if (redrawn) skipScriptFocus(track);

    var queued = false;
    track.addEventListener(
      'scroll',
      function () {
        if (isShown(track)) savedScroll[key] = track.scrollLeft;
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(function () {
          queued = false;
          update(row);
        });
      },
      { passive: true }
    );

    row.addEventListener('click', function (event) {
      var nudge = event.target.closest && event.target.closest('[data-empiric-color-nudge]');
      if (!nudge || !row.contains(nudge)) return;
      event.preventDefault();
      var direction = nudge.getAttribute('data-empiric-color-nudge') === 'prev' ? -1 : 1;
      scrollTrack(track, track.scrollLeft + direction * Math.max(120, track.clientWidth * 0.6), true);
    });

    if (window.ResizeObserver) {
      new ResizeObserver(function () {
        update(row);
      }).observe(track);
    }

    bindDrag(row, track);
  }

  function scan(root, redrawn) {
    if (!root || !root.querySelectorAll) return;
    if (root.matches && root.matches('[data-empiric-color-row]')) bind(root, redrawn);
    var rows = root.querySelectorAll('[data-empiric-color-row]');
    for (var i = 0; i < rows.length; i++) bind(rows[i], redrawn);
  }

  function updateAll() {
    var rows = document.querySelectorAll('[data-empiric-color-row]');
    for (var i = 0; i < rows.length; i++) update(rows[i]);
  }

  function start() {
    scan(document, false);

    if (window.MutationObserver) {
      new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            if (added[j].nodeType === 1) scan(added[j], true);
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }

    // Keep the picked color in view as soon as it is picked (mouse, touch or arrow keys).
    document.addEventListener('change', function (event) {
      var target = event.target;
      if (!target || target.type !== 'radio' || !target.closest) return;
      var row = target.closest('[data-empiric-color-row]');
      if (row) reveal(row, true);
    });

    // Pill widths change once the web fonts arrive.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(updateAll);
    window.addEventListener('load', updateAll);
    if (!window.ResizeObserver) window.addEventListener('resize', updateAll);
  }

  window.EmpiricColorRow = { refresh: updateAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();